/**
 * ProjectDetail — project-level management hub.
 *
 * Tabs:
 *   Access          — member allocation + department lead assignment
 *   Section config  — which RFP section types apply to this project
 *   Bid reviewer    — bids + approval chain status; assign reviewers
 *   Submitted bids  — bids in submitted/awarded/lost stage; links to tender Outcome tab
 *   Settings        — tags, importance, mark completed
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  Crown,
  ExternalLink,
  FileText,
  Globe2,
  Hash,
  History,
  IndianRupee,
  Layers,
  Loader2,
  MessageSquare,
  Settings2,
  ShieldCheck,
  Star,
  Upload,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectAccess } from "@/hooks/useProjectAccess";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

import MembersPanel from "@/components/v2/MembersPanel";
import WinProbExplainer from "@/components/v2/WinProbExplainer";
import ProjectSectionConfigPage from "@/pages/v2/ProjectSectionConfigPage";

// ── small helpers ─────────────────────────────────────────────────────────────

function StatPill({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 text-sm text-slate-600">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-slate-500 shrink-0">{label}:</span>
      <span className="font-medium text-slate-800 truncate">{value}</span>
    </div>
  );
}

function _fmtINR(n) {
  if (n == null || isNaN(n)) return null;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

function moneyFmtINR(n, currency) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

const STAGE_TONE = {
  bid_published:   "bg-slate-100 text-slate-600",
  go_no_go:        "bg-amber-100 text-amber-800",
  preparing:       "bg-blue-100 text-blue-700",
  drafting:        "bg-blue-100 text-blue-700",
  ready_to_submit: "bg-indigo-100 text-indigo-700",
  submitted:       "bg-violet-100 text-violet-700",
  awarded:         "bg-emerald-100 text-emerald-700",
  won:             "bg-emerald-100 text-emerald-700",
  lost:            "bg-rose-100 text-rose-700",
  archived:        "bg-slate-100 text-slate-500",
};

const IMPORTANCE_LEVELS = [
  { value: "low",      label: "Low",      cls: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "medium",   label: "Medium",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "high",     label: "High",     cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { value: "critical", label: "Critical", cls: "bg-rose-50 text-rose-700 border-rose-200" },
];



// ── BidRow ────────────────────────────────────────────────────────────────────

function BidRow({ bid, navigate, showReviewerInfo = false }) {
  const stageKey = (bid.stage || "").toLowerCase();
  const stageTone = STAGE_TONE[stageKey] || "bg-slate-100 text-slate-700";
  const approvals = bid.approval_status || {};
  const total = Object.keys(approvals).length;
  const approved = Object.values(approvals).filter((v) => v === "approved").length;
  const pending = Object.values(approvals).filter((v) => v === "pending").length;
  const winPct = bid.win_probability_pct;

  return (
    <div className="flex items-center gap-3 px-2 py-2.5 hover:bg-slate-50 rounded-md">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800 truncate">{bid.name}</span>
          <Badge className={`text-[10px] font-mono uppercase ${stageTone} border-0`}>
            {stageKey.replace(/_/g, " ")}
          </Badge>
          {bid.decision && bid.decision !== "undecided" && (
            <Badge variant="outline" className="text-[10px] uppercase">
              {bid.decision.replace(/_/g, " ")}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500 mt-0.5 flex-wrap">
          {winPct != null && (
            <span className={`tabular-nums font-medium ${winPct >= 65 ? "text-emerald-600" : winPct >= 45 ? "text-amber-600" : "text-rose-600"}`}>
              Win {Math.round(winPct)}%
            </span>
          )}
          {bid.submitted_value_inr && (
            <span className="tabular-nums">{_fmtINR(bid.submitted_value_inr)}</span>
          )}
          {showReviewerInfo && total > 0 && (
            <span className="tabular-nums">
              Approvals {approved}/{total}
              {pending > 0 && <span className="text-amber-600 ml-1">({pending} pending)</span>}
            </span>
          )}
          {bid.version > 1 && <span>v{bid.version}</span>}
        </div>
      </div>
      {winPct != null && <WinProbExplainer bid={bid} />}
      {bid.rfp_id && (
        <Button size="sm" variant="ghost" className="text-blue-600 hover:bg-blue-50 shrink-0"
          onClick={() => navigate(`/tender/${bid.rfp_id}`)}>
          Open tender <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
}


// ── BidsAndQuotationsPanel ────────────────────────────────────────────────────

const QUOT_STATUS_CLS = {
  draft: "bg-slate-100 text-slate-600",
  pending_approval: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  sent: "bg-blue-100 text-blue-700",
  rejected: "bg-rose-100 text-rose-700",
  withdrawn: "bg-slate-100 text-slate-500",
  expired: "bg-slate-100 text-slate-500",
};

function BidsAndQuotationsPanel({ bids, navigate }) {
  const [quotsByBid, setQuotsByBid] = useState({});

  useEffect(() => {
    if (!bids.length) return;
    const bidsWithRfp = bids.filter((b) => b.rfp_id && b.id);
    Promise.allSettled(
      bidsWithRfp.map((b) =>
        v2Api.quotations.listForBid(b.id).then((qs) => ({ bidId: b.id, qs }))
      )
    ).then((results) => {
      const map = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") map[r.value.bidId] = r.value.qs || [];
      });
      setQuotsByBid(map);
    });
  }, [bids]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bids.length) return null;

  return (
    <Card>
      <CardHeader className="pb-1 pt-3 px-4">
        <CardTitle className="text-sm text-slate-600 font-medium">Bids & Quotations</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="divide-y divide-slate-100 -mx-2">
          {bids.map((b) => {
            const quots = quotsByBid[b.id] || [];
            const latest = quots.length
              ? quots.reduce((a, c) => (c.version > (a?.version || 0) ? c : a), null)
              : null;
            return (
              <div key={b.id} className="py-2.5 px-2 hover:bg-slate-50 rounded-md">
                {/* Bid info row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800 truncate">{b.name}</span>
                  <Badge className={`text-[10px] font-mono uppercase border-0 ${STAGE_TONE[(b.stage || "").toLowerCase()] || "bg-slate-100 text-slate-700"}`}>
                    {(b.stage || "").toLowerCase().replace(/_/g, " ")}
                  </Badge>
                  {b.decision && b.decision !== "undecided" && (
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {b.decision.replace(/_/g, " ")}
                    </Badge>
                  )}
                  {b.win_probability_pct != null && (
                    <span className={`text-xs font-medium tabular-nums ${b.win_probability_pct >= 65 ? "text-emerald-600" : b.win_probability_pct >= 45 ? "text-amber-600" : "text-rose-600"}`}>
                      Win {Math.round(b.win_probability_pct)}%
                    </span>
                  )}
                </div>
                {/* Quotation row */}
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {latest ? (
                    <>
                      <span className="text-xs text-slate-500 font-mono">{latest.quotation_no} v{latest.version}</span>
                      <Badge className={`text-[10px] border-0 ${QUOT_STATUS_CLS[latest.status] || "bg-slate-100 text-slate-600"}`}>
                        {latest.status}
                      </Badge>
                      {latest.grand_total > 0 && (
                        <span className="text-xs font-medium text-slate-700 tabular-nums">
                          {_fmtINR(latest.grand_total)}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-slate-400 italic">No quotation yet</span>
                  )}
                  {b.rfp_id && (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline ml-auto flex items-center gap-1"
                      onClick={() => navigate(`/tender/${b.rfp_id}?tab=quotation`)}
                    >
                      Open quotation <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


// ── ProjectDepartmentsPanel ───────────────────────────────────────────────────

function ProjectDepartmentsPanel({ projectId, navigate, canManage = false }) {
  const [attached, setAttached] = useState([]);
  const [allDepts, setAllDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState("");

  const load = async () => {
    const [aRes, dRes] = await Promise.allSettled([
      v2Api.projects.departments(projectId),
      v2Api.departments(),
    ]);
    if (aRes.status === "fulfilled") setAttached(aRes.value || []);
    if (dRes.status === "fulfilled") setAllDepts(dRes.value || []);
  };

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachedIds = new Set(attached.map((d) => d.department_id));
  const available = allDepts.filter(
    (d) => d.is_active !== false && !attachedIds.has(d.id)
  );

  const attach = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await v2Api.projects.attachDepartment(projectId, selected);
      toast.success("Department attached");
      setAdding(false);
      setSelected("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not attach");
    } finally { setBusy(false); }
  };

  const detach = async (deptId, deptName) => {
    if (!await confirm(`Remove ${deptName} from this project?`, { title: "Remove department", confirmLabel: "Remove" })) return;
    setBusy(true);
    try {
      await v2Api.projects.detachDepartment(projectId, deptId);
      toast.success("Department removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not remove");
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" /> Departments
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              All members of an attached department get access to this project.
              To change who leads a department, go to{" "}
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => navigate("/v2/departments")}
              >
                Departments
              </button>.
            </CardDescription>
          </div>
          {canManage && available.length > 0 && !adding && (
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              + Attach department
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <select
              autoFocus
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="flex-1 text-sm px-3 py-1.5 border border-slate-200 rounded-md bg-white"
            >
              <option value="">— pick a department —</option>
              {available.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <Button size="sm" onClick={attach} disabled={busy || !selected}>
              Attach
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setSelected(""); }}>
              Cancel
            </Button>
          </div>
        )}

        {attached.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">
            No departments attached yet. Click "Attach department" to give a whole team access.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {attached.map((d) => (
              <div key={d.department_id} className="flex items-center gap-3 py-2.5">
                <div
                  className="w-2 h-8 rounded-full shrink-0"
                  style={{ background: d.department_color || "#94a3b8" }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-800">{d.department_name}</p>
                  <p className="text-xs text-slate-500">
                    {d.lead_user_name
                      ? <>Lead: <span className="font-medium text-slate-700">{d.lead_user_name}</span></>
                      : <span className="italic">No lead assigned</span>}
                    {d.member_count > 0 && (
                      <span className="ml-2 text-slate-400">· {d.member_count} member{d.member_count !== 1 ? "s" : ""}</span>
                    )}
                  </p>
                </div>
                {canManage && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => detach(d.department_id, d.department_name)}
                    className="text-slate-400 hover:text-rose-500"
                    title="Remove department"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ── ProjectLeadCard ───────────────────────────────────────────────────────────

function avatarInitials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
}

function leadColorFor(seed) {
  const palette = [
    "bg-blue-100 text-blue-800",
    "bg-emerald-100 text-emerald-800",
    "bg-amber-100 text-amber-800",
    "bg-rose-100 text-rose-800",
    "bg-violet-100 text-violet-800",
    "bg-teal-100 text-teal-800",
  ];
  let h = 0;
  for (const c of String(seed)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}

function ProjectLeadCard({ project, onChange, readOnly = false }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    v2Api.workspaceMembers()
      .then((all) => setUsers((all || []).filter((u) => u.is_active !== false)))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q
      ? users.filter((u) =>
          u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
        )
      : users;
  }, [users, search]);

  const currentLead = users.find((u) => u.id === project.project_lead_user_id);

  const save = async (userId) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await v2Api.projects.patch(project.id, {
        project_lead_user_id: userId || null,
      });
      onChange(updated);
      toast.success(userId ? "Project lead assigned" : "Project lead removed");
      setOpen(false);
      setSearch("");
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-500" /> Project lead
        </CardTitle>
        <CardDescription className="text-xs">
          Accountable for this project's bid outcome. Gets manager-level access scoped to this project.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Current lead display */}
        {currentLead ? (
          <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-100">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${leadColorFor(currentLead.email)}`}>
              {avatarInitials(currentLead.name)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{currentLead.name}</p>
              <p className="text-xs text-slate-400 truncate">{currentLead.email}</p>
            </div>
            <Badge variant="outline" className={`text-[11px] shrink-0 ${
              currentLead.workspace_role_code === "manager"
                ? "border-blue-300 text-blue-700 bg-blue-50"
                : currentLead.workspace_role_code === "admin"
                ? "border-amber-300 text-amber-700 bg-amber-50"
                : ""
            }`}>
              {currentLead.workspace_role_name || currentLead.legacy_role || "member"}
            </Badge>
            {!readOnly && (
              <button type="button" onClick={() => save("")} disabled={busy}
                className="text-slate-400 hover:text-rose-500 transition" title="Remove lead">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">No lead assigned</p>
        )}

        {/* Toggle picker — admins only */}
        {!readOnly && (
          <Button
            size="sm" variant="outline" className="w-full"
            onClick={() => { setOpen((v) => !v); setSearch(""); }}
          >
            {open ? "Cancel" : currentLead ? "Change lead" : "Assign lead"}
          </Button>
        )}

        {/* Search + list — only shown when open */}
        {!readOnly && open && (
          <>
            <Input
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="border rounded-md overflow-y-auto max-h-52">
              {filtered.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-5">No users found</p>
              ) : (
                filtered.map((u) => {
                  const isActive = u.id === project.project_lead_user_id;
                  return (
                    <button
                      key={u.id} type="button" disabled={busy}
                      onClick={() => save(u.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition hover:bg-slate-50 ${
                        isActive ? "bg-amber-50" : ""
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${leadColorFor(u.email)}`}>
                        {avatarInitials(u.name)}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">{u.name}</span>
                        <span className="block text-xs text-slate-400 truncate">{u.email}</span>
                      </span>
                      <Badge variant="outline" className={`text-[11px] shrink-0 ${
                        u.workspace_role_code === "manager"
                          ? "border-blue-300 text-blue-700 bg-blue-50"
                          : u.workspace_role_code === "admin"
                          ? "border-amber-300 text-amber-700 bg-amber-50"
                          : ""
                      }`}>
                        {u.workspace_role_name || u.legacy_role || "member"}
                      </Badge>
                      {isActive && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


// ── tab: RFPs ─────────────────────────────────────────────────────────────────

function RfpsTab({ projectId, navigate, canUpload = false }) {
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = React.useRef(null);

  const load = () => {
    setLoading(true);
    v2Api.rfp.listForProject(projectId)
      .then((r) => setRfps(r || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 300 * 1024 * 1024) { toast.error("File exceeds 300 MB"); return; }
    const form = new FormData();
    form.append("project_id", projectId);
    form.append("title", file.name);
    form.append("file", file);
    form.append("doc_type", "rfp");
    form.append("analyze_now", "true");
    setBusy(true);
    setProgress(0);
    try {
      const r = await v2Api.rfp.upload(form, (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      });
      toast.success(`Uploaded — ${r.pages || 0} pages`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? err?.message ?? "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload area */}
      {canUpload && <div
        className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
        onClick={() => !busy && fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx,.xlsx,.xls,.zip"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {busy ? (
          <div className="space-y-2">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
            <p className="text-sm text-slate-600">Uploading &amp; analysing… {progress}%</p>
            <div className="w-full bg-slate-100 rounded-full h-1.5 max-w-xs mx-auto">
              <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <>
            <Upload className="w-7 h-7 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">Drop RFP / tender document here</p>
            <p className="text-xs text-slate-400 mt-0.5">or click to browse · PDF, Word, Excel, ZIP · up to 300 MB</p>
          </>
        )}
      </div>}

      {/* RFP list */}
      {loading && <p className="text-sm text-slate-400">Loading…</p>}
      {!loading && rfps.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">No RFPs uploaded yet.</p>
      )}
      <div className="space-y-2">
        {rfps.map((r) => (
          <div
            key={r.id}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
            onClick={() => navigate(`/tender/${r.id}`)}
          >
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{r.title || r.id}</p>
              <p className="text-xs text-slate-400">{r.doc_type || "rfp"}{r.pages ? ` · ${r.pages} pages` : ""}{r.parse_status ? ` · ${r.parse_status}` : ""}</p>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-slate-300 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}


// ── tab: Settings ─────────────────────────────────────────────────────────────

const CLIENT_TYPES = [
  { value: "central_govt", label: "Central Govt" },
  { value: "state_govt",   label: "State Govt" },
  { value: "psu",          label: "PSU" },
  { value: "private",      label: "Private" },
  { value: "foreign",      label: "Foreign" },
];

const TENDER_PORTALS = [
  { value: "gem",          label: "GeM" },
  { value: "cppp",         label: "CPPP / eProcure" },
  { value: "state_portal", label: "State Portal" },
  { value: "private",      label: "Private" },
  { value: "foreign",      label: "International" },
  { value: "other",        label: "Other" },
];

const CURRENCIES = [
  { value: "INR", label: "INR" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "AED", label: "AED" },
];

function SettingsTab({ project, onChange, onMarkCompleted, navigate, canMarkCompleted = false, canManage = false }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const tags = project.tags || [];
  const importance = project.importance || "";

  // Project details form state — synced from project on mount / project change
  const [details, setDetails] = useState({
    code: project.code || "",
    name: project.name || "",
    summary: project.summary || "",
    client_name: project.client_name || "",
    client_type: project.client_type || "",
    client_state: project.client_state || "",
    tender_portal: project.tender_portal || "",
    currency: project.currency || "INR",
    estimated_value: project.estimated_value != null ? String(project.estimated_value) : "",
    submission_deadline: project.submission_deadline || "",
  });
  const [detailsDirty, setDetailsDirty] = useState(false);

  // Keep form in sync if parent project object changes (e.g. after external save)
  useEffect(() => {
    setDetails({
      code: project.code || "",
      name: project.name || "",
      summary: project.summary || "",
      client_name: project.client_name || "",
      client_type: project.client_type || "",
      client_state: project.client_state || "",
      tender_portal: project.tender_portal || "",
      currency: project.currency || "INR",
      estimated_value: project.estimated_value != null ? String(project.estimated_value) : "",
      submission_deadline: project.submission_deadline || "",
    });
    setDetailsDirty(false);
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDetail = (field, value) => {
    setDetails((d) => ({ ...d, [field]: value }));
    setDetailsDirty(true);
  };

  const saveDetails = async () => {
    if (!details.code.trim() || !details.name.trim()) {
      toast.error("Code and name are required");
      return;
    }
    setBusy(true);
    try {
      const patch = {
        ...details,
        estimated_value: details.estimated_value ? Number(details.estimated_value) : null,
        // Send empty strings as null so the backend clears optional fields
        client_type: details.client_type || null,
        client_state: details.client_state || null,
        tender_portal: details.tender_portal || null,
        summary: details.summary || null,
        submission_deadline: details.submission_deadline || null,
      };
      const updated = await v2Api.projects.patch(project.id, patch);
      onChange(updated);
      setDetailsDirty(false);
      toast.success("Project details saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const save = async (patch) => {
    setBusy(true);
    try {
      const updated = await v2Api.projects.patch(project.id, patch);
      onChange(updated);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const addTag = () => {
    const t = draft.trim().replace(/^#+/, "").toLowerCase();
    if (!t || tags.includes(t)) { setDraft(""); return; }
    save({ tags: [...tags, t] });
    setDraft("");
  };

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Project details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-slate-500" /> Project details
          </CardTitle>
          {!canManage && (
            <CardDescription className="text-xs">
              Only admins, managers, and the project lead can edit project details.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Project code</Label>
              {canManage ? (
                <Input
                  value={details.code}
                  onChange={(e) => setDetail("code", e.target.value.toUpperCase())}
                  placeholder="AXI-2026-014"
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1 font-mono">{project.code || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Name</Label>
              {canManage ? (
                <Input
                  value={details.name}
                  onChange={(e) => setDetail("name", e.target.value)}
                  placeholder="Project name"
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1">{project.name || "—"}</p>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500 mb-1 block">Summary</Label>
              {canManage ? (
                <Input
                  value={details.summary}
                  onChange={(e) => setDetail("summary", e.target.value)}
                  placeholder="One-line description for the team"
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1">{project.summary || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Client</Label>
              {canManage ? (
                <Input
                  value={details.client_name}
                  onChange={(e) => setDetail("client_name", e.target.value)}
                  placeholder="Client organisation"
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1">{project.client_name || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Client type</Label>
              {canManage ? (
                <Select value={details.client_type} onValueChange={(v) => setDetail("client_type", v)}>
                  <SelectTrigger disabled={busy}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {CLIENT_TYPES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-slate-800 py-1">
                  {CLIENT_TYPES.find((c) => c.value === project.client_type)?.label || project.client_type || "—"}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Tender portal</Label>
              {canManage ? (
                <Select value={details.tender_portal} onValueChange={(v) => setDetail("tender_portal", v)}>
                  <SelectTrigger disabled={busy}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {TENDER_PORTALS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-slate-800 py-1">
                  {TENDER_PORTALS.find((p) => p.value === project.tender_portal)?.label || project.tender_portal || "—"}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Currency</Label>
              {canManage ? (
                <Select value={details.currency} onValueChange={(v) => setDetail("currency", v)}>
                  <SelectTrigger disabled={busy}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-slate-800 py-1">{project.currency || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Estimated value</Label>
              {canManage ? (
                <Input
                  type="number"
                  value={details.estimated_value}
                  onChange={(e) => setDetail("estimated_value", e.target.value)}
                  placeholder="165000000"
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1">
                  {project.estimated_value != null
                    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: project.currency || "INR", maximumFractionDigits: 0 }).format(project.estimated_value)
                    : "—"}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Buyer state code (GST)</Label>
              {canManage ? (
                <Input
                  value={details.client_state}
                  onChange={(e) => setDetail("client_state", e.target.value)}
                  placeholder="07 (Delhi), 27 (Maharashtra)…"
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1">{project.client_state || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Submission deadline</Label>
              {canManage ? (
                <Input
                  type="date"
                  value={details.submission_deadline}
                  onChange={(e) => setDetail("submission_deadline", e.target.value)}
                  disabled={busy}
                />
              ) : (
                <p className="text-sm text-slate-800 py-1">{project.submission_deadline || "—"}</p>
              )}
            </div>
          </div>
          {canManage && (
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={saveDetails} disabled={busy || !detailsDirty}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Save details
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tags + importance */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Hash className="w-4 h-4 text-slate-500" /> Tags &amp; importance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Tags</Label>
            <div className="flex flex-wrap items-center gap-2">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                  #{t}
                  <button type="button" onClick={() => save({ tags: tags.filter((x) => x !== t) })}
                    disabled={busy} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                onBlur={addTag}
                placeholder="add tag…"
                className="text-xs px-2 py-0.5 border border-slate-200 rounded-full bg-white outline-none focus:border-blue-400 w-24"
                disabled={busy} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Importance</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {IMPORTANCE_LEVELS.map((lvl) => (
                <button key={lvl.value} type="button" disabled={busy}
                  onClick={() => save({ importance: importance === lvl.value ? null : lvl.value })}
                  className={`px-3 py-1 rounded-full text-xs border transition ${
                    importance === lvl.value ? lvl.cls : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                  }`}>
                  {lvl.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mark completed — danger zone */}
      {canMarkCompleted && project.status !== "closed" && (
        <Card className="border-rose-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-rose-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Mark project completed
            </CardTitle>
            <CardDescription className="text-xs">
              Closes the project and freezes all bids. Cannot be undone without admin access.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" size="sm" onClick={onMarkCompleted}>
              ✓ Mark project completed
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ── DecideDialog ──────────────────────────────────────────────────────────────

function DecideDialog({ quotation, intent, onClose, onDecided }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const isApprove = intent === "approved";

  const submit = async () => {
    setBusy(true);
    try {
      const updated = await v2Api.quotations.setStatus(quotation.id, intent, note.trim() || null);
      toast.success(`Quotation ${quotation.quotation_no} ${isApprove ? "approved" : "rejected"}`);
      onDecided(updated);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Decision failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isApprove ? "text-emerald-700" : "text-rose-700"}`}>
            {isApprove
              ? <><Check className="w-4 h-4" /> Approve quotation</>
              : <><X className="w-4 h-4" /> Reject quotation</>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="rounded-md bg-slate-50 border px-3 py-2 text-sm">
            <span className="font-medium">{quotation.quotation_no}</span>
            <span className="text-slate-400 ml-1.5">v{quotation.version}</span>
            <span className="ml-3 font-mono font-semibold text-blue-700">
              {moneyFmtINR(quotation.grand_total, quotation.currency)}
            </span>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Note <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              rows={3}
              placeholder={isApprove ? "Any conditions or remarks…" : "Reason for rejection…"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="text-sm resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            size="sm"
            disabled={busy}
            className={isApprove ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
            onClick={submit}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : isApprove ? <Check className="w-3.5 h-3.5 mr-1" /> : <X className="w-3.5 h-3.5 mr-1" />}
            {busy ? "Saving…" : isApprove ? "Approve" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── tab: Bid reviewer ─────────────────────────────────────────────────────────

function BidReviewerTab({ bids, projectId, navigate, canManage = false }) {
  const [pendingQuots, setPendingQuots] = useState([]);
  const [historyQuots, setHistoryQuots] = useState([]);
  const [quotsLoading, setQuotsLoading] = useState(true);
  const [decideTarget, setDecideTarget] = useState(null); // { quotation, intent }

  const loadQuots = () => {
    if (!projectId) return;
    setQuotsLoading(true);
    Promise.all([
      v2Api.projects.quotations(projectId, { status: "pending_approval" }),
      v2Api.projects.quotations(projectId, { status: "approved" }),
      v2Api.projects.quotations(projectId, { status: "rejected" }),
    ])
      .then(([pending, approved, rejected]) => {
        setPendingQuots(pending || []);
        const history = [...(approved || []), ...(rejected || [])].sort(
          (a, b) => new Date(b.reviewed_at || b.created_at) - new Date(a.reviewed_at || a.created_at)
        );
        setHistoryQuots(history);
      })
      .catch(() => {})
      .finally(() => setQuotsLoading(false));
  };

  useEffect(() => { loadQuots(); }, [projectId]);

  const handleDecided = (updated) => {
    setPendingQuots((prev) => prev.filter((x) => x.id !== updated.id));
    setHistoryQuots((prev) => {
      const without = prev.filter((x) => x.id !== updated.id);
      return [updated, ...without];
    });
  };

  const activeBids = bids.filter((b) => {
    const s = (b.stage || "").toLowerCase();
    return !["submitted", "awarded", "won", "lost", "archived"].includes(s);
  });

  return (
    <div className="space-y-6">
      {/* ── Quotations in review ────────────────────────────────────── */}
      <div>
        <p className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-amber-600" /> Quotations pending review
        </p>
        {quotsLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : pendingQuots.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-3">
            No quotations pending review on this project.
          </p>
        ) : (
          <div className="border rounded-lg divide-y bg-white overflow-hidden">
            {pendingQuots.map((q) => (
              <div key={q.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium text-sm">
                    {q.quotation_no}{" "}
                    <span className="text-slate-400 font-normal text-xs">v{q.version}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Sent for review by{" "}
                    <span className="font-medium text-slate-700">
                      {q.submitted_by_name || q.submitted_by_email || "Unknown"}
                    </span>
                    {q.submitted_for_review_at && (
                      <> · {new Date(q.submitted_for_review_at).toLocaleDateString("en-IN")}</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="font-mono font-semibold text-blue-700 text-sm">
                    {moneyFmtINR(q.grand_total, q.currency)}
                  </p>
                  {canManage ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => setDecideTarget({ quotation: q, intent: "rejected" })}
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => setDecideTarget({ quotation: q, intent: "approved" })}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Pending review</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Review history ───────────────────────────────────────────── */}
      {!quotsLoading && historyQuots.length > 0 && (
        <div>
          <p className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" /> Review history
          </p>
          <div className="border rounded-lg divide-y bg-white overflow-hidden">
            {historyQuots.map((q) => {
              const isApproved = q.status === "approved";
              return (
                <div key={q.id} className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      isApproved ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"
                    }`}>
                      {isApproved ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">
                        {q.quotation_no}{" "}
                        <span className="text-slate-400 font-normal text-xs">v{q.version}</span>
                        <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          isApproved ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"
                        }`}>
                          {isApproved ? "Approved" : "Rejected"}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {isApproved ? "Approved" : "Rejected"} by{" "}
                        <span className="font-medium text-slate-700">
                          {q.reviewed_by_name || q.reviewed_by_email || "Unknown"}
                        </span>
                        {q.reviewed_at && (
                          <> · {new Date(q.reviewed_at).toLocaleDateString("en-IN")}</>
                        )}
                      </p>
                      {q.review_note && (
                        <p className="text-xs text-slate-600 mt-1 flex items-start gap-1">
                          <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                          {q.review_note}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="font-mono font-semibold text-blue-700 text-sm shrink-0">
                    {moneyFmtINR(q.grand_total, q.currency)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Active bids ─────────────────────────────────────────────── */}
      {activeBids.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">
          No active bids pending review on this project.
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Bids currently in preparation. Artifact approvals show how many
            documents have been reviewed. Open a tender to manage the approval chain.
          </p>
          <div className="divide-y divide-slate-100 border rounded-lg overflow-hidden bg-white">
            {activeBids.map((b) => (
              <div key={b.id} className="px-2">
                <BidRow bid={b} navigate={navigate} showReviewerInfo />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">
            To configure which roles review each artifact type, go to Settings →
            Approval policies.
          </p>
        </div>
      )}

      {decideTarget && (
        <DecideDialog
          quotation={decideTarget.quotation}
          intent={decideTarget.intent}
          onClose={() => setDecideTarget(null)}
          onDecided={handleDecided}
        />
      )}
    </div>
  );
}



// ── main page ─────────────────────────────────────────────────────────────────

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [bids, setBids] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [tab, setTab] = useState("rfps");
  const access = useProjectAccess(project, projectMembers);

  const loadAll = async () => {
    setBids([]);
    const [pRes, bRes, mRes] = await Promise.allSettled([
      v2Api.projects.get(id),
      v2Api.projects.bids(id),
      v2Api.projects.members(id),
    ]);
    if (pRes.status === "fulfilled") setProject(pRes.value);
    else { toast.error("Could not load project"); return; }
    if (bRes.status === "fulfilled") setBids(bRes.value);
    if (mRes.status === "fulfilled") setProjectMembers(mRes.value || []);
  };

  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markCompleted = async () => {
    if (!await confirm("Mark this project as completed? This closes all bids.", { title: "Mark project completed", confirmLabel: "Mark completed", destructive: false })) return;
    try {
      await v2Api.projects.markCompleted(project.id);
      toast.success("Project marked completed");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not mark complete");
    }
  };

  if (!project) {
    return <div className="p-8 text-sm text-slate-500">Loading project…</div>;
  }

  return (
    <div className="px-6 py-5 max-w-[1300px] mx-auto space-y-5 relative">

      <Button variant="ghost" size="sm" onClick={() => navigate("/v2/projects")}
        className="text-slate-500 -ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Projects
      </Button>

      {/* ── compact project header ─────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            {project.code}
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2 flex-wrap">
            {project.name}
            {project.status === "closed" && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                ✓ Completed
              </Badge>
            )}
          </h1>
          {project.summary && (
            <p className="text-slate-500 text-sm mt-1 max-w-2xl">{project.summary}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2">
            {project.client_name && (
              <StatPill
                icon={project.client_type === "foreign" ? Globe2 : Building2}
                label="Client" value={project.client_name} />
            )}
            {project.tender_portal && (
              <StatPill icon={Briefcase} label="Portal"
                value={project.tender_portal.toUpperCase()} />
            )}
            {project.estimated_value && (
              <StatPill icon={IndianRupee} label="Est. value"
                value={new Intl.NumberFormat("en-IN", {
                  style: "currency", currency: project.currency || "INR",
                  maximumFractionDigits: 0,
                }).format(project.estimated_value)} />
            )}
            {project.submission_deadline && (
              <StatPill icon={Calendar} label="Deadline"
                value={project.submission_deadline} />
            )}
            {project.importance && (
              <StatPill icon={Star} label="Importance"
                value={project.importance.toUpperCase()} />
            )}
            {project.project_lead_user_name && (
              <StatPill icon={Crown} label="Lead"
                value={project.project_lead_user_name} />
            )}
          </div>
        </div>
      </header>

      {/* ── management tabs ────────────────────────────────────────────── */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="rfps" className="flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> RFPs
          </TabsTrigger>
          <TabsTrigger value="access" className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Access
          </TabsTrigger>
          <TabsTrigger value="section-config" className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> Section config
          </TabsTrigger>
          <TabsTrigger value="bid-reviewer" className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Bid reviewer
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-1.5">
            <Settings2 className="w-3.5 h-3.5" /> Settings
          </TabsTrigger>
        </TabsList>

        {/* RFPs ─────────────────────────────────────────────────────────── */}
        <TabsContent value="rfps" className="mt-4">
          <RfpsTab projectId={project.id} navigate={navigate} canUpload={access.adminManagerOrPM} />
        </TabsContent>

        {/* Access — project lead + departments + direct members ─────────── */}
        <TabsContent value="access" className="mt-4 space-y-5">
          <ProjectLeadCard project={project} onChange={setProject} readOnly={!access.adminAndManager} />
          <ProjectDepartmentsPanel
            projectId={project.id}
            navigate={navigate}
            canManage={access.adminManagerOrPM}
          />
          <MembersPanel
            project={project}
            canManage={access.adminManagerOrPM}
            onMembersChange={setProjectMembers}
          />
        </TabsContent>

        {/* Section config ───────────────────────────────────────────────── */}
        <TabsContent value="section-config" className="mt-4">
          <ProjectSectionConfigPage projectId={project.id} compact />
        </TabsContent>

        {/* Bid reviewer ─────────────────────────────────────────────────── */}
        <TabsContent value="bid-reviewer" className="mt-4">
          <BidReviewerTab
            bids={bids}
            projectId={project.id}
            navigate={navigate}
            canManage={access.adminManagerOrPM}
          />
        </TabsContent>


        {/* Settings ─────────────────────────────────────────────────────── */}
        <TabsContent value="settings" className="mt-4">
          <SettingsTab project={project} onChange={setProject}
            onMarkCompleted={markCompleted} navigate={navigate}
            canMarkCompleted={access.adminManagerOrPM}
            canManage={access.adminManagerOrPM} />
        </TabsContent>
      </Tabs>

    </div>
  );
} 

